import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Filter, XCircle, CheckCircle2, AlertTriangle, Circle } from "lucide-react";
import api from "../../lib/api";
import StatusPill from "../merchant/components/StatusPill";
import CancelMerchantsModal from "./components/CancelMerchantsModal";
import SLAPill from "./components/SLAPill";

// Brief 3.5 PR 3 §3.1 — verdict icon column. Icons map to the four
// rule-table outcomes (✓ ⚠ ✗ ○) the verdict service emits. Compute on
// demand per founder instruction — no memoization, no client cache.
const VERDICT_ICONS = {
  verified: { Icon: CheckCircle2,  cls: "text-emerald-300", label: "Verified" },
  review:   { Icon: AlertTriangle, cls: "text-amber-300",   label: "Review"   },
  blocker:  { Icon: XCircle,       cls: "text-rose-300",    label: "Blocker"  },
  missing:  { Icon: Circle,        cls: "text-zinc-500",    label: "Missing"  },
};

function VerdictIcon({ verdict }) {
  const key = verdict?.icon || "missing";
  const meta = VERDICT_ICONS[key] || VERDICT_ICONS.missing;
  const Icon = meta.Icon;
  // Tooltip surfaces primary_blocker for blocker icons so the operator
  // doesn't need to click into the merchant to know why it's flagged.
  const title = verdict?.primary_blocker
    ? `${meta.label}: ${verdict.primary_blocker}`
    : meta.label;
  return (
    <Icon
      className={`h-4 w-4 ${meta.cls}`}
      data-testid={`merchant-verdict-icon-${key}`}
      aria-label={title}
      title={title}
    />
  );
}

// Brief 3.6.5 (B) — `cancelled` is its own filter pill. It's the only
// way to surface AML-retained soft-deleted merchants since the default
// list hides them server-side.
const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "awaiting_documents", label: "Awaiting docs" },
  { key: "submitted", label: "Submitted" },
  { key: "under_review", label: "Under review" },
  { key: "info_requested", label: "Info requested" },
  { key: "approved", label: "Approved" },
  { key: "live", label: "Live" },
  { key: "declined", label: "Declined" },
  { key: "cancelled", label: "Cancelled" },
];

// Cancellable from any non-terminal state (mirrors backend
// services/merchant_states.CANCELLABLE_STATES). Used by the row checkbox
// to disable + tooltip non-cancellable rows (already live / terminated /
// already cancelled).
const CANCELLABLE_STATES = new Set([
  "prospect", "draft", "awaiting_documents", "submitted",
  "under_review", "info_requested", "approved", "declined",
]);

export default function AdminMerchants() {
  const [items, setItems] = useState([]);
  const [verdicts, setVerdicts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = filter ? { status: filter } : undefined;
        const { data } = await api.get("/admin/merchants", { params });
        if (cancelled) return;
        const rows = data.items || [];
        setItems(rows);
        // Cancellation invalidates prior selection — never carry a
        // selected merchant_id across reloads.
        setSelectedIds(new Set());
        setVerdicts({});
        // Brief 3.5 PR 3 — bulk verdict fetch (max 100 ids per request,
        // backend caps + chunks defensively). Compute on demand per
        // founder instruction — no memoization across reloads.
        if (rows.length) {
          const chunks = [];
          for (let i = 0; i < rows.length; i += 100) {
            chunks.push(rows.slice(i, i + 100).map((r) => r.merchant_id).join(","));
          }
          const results = await Promise.all(chunks.map((ids) =>
            api.get("/admin/verdicts/bulk", { params: { ids } })
              .then((r) => r.data?.verdicts || {})
              .catch(() => ({})),
          ));
          if (cancelled) return;
          setVerdicts(Object.assign({}, ...results));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, reloadKey]);

  const selectableItems = useMemo(
    () => items.filter((m) => CANCELLABLE_STATES.has(m.status)),
    [items],
  );
  const allSelectableSelected = selectableItems.length > 0
    && selectableItems.every((m) => selectedIds.has(m.merchant_id));
  const someSelected = selectedIds.size > 0;

  const toggleOne = (mid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(() => {
      if (allSelectableSelected) return new Set();
      return new Set(selectableItems.map((m) => m.merchant_id));
    });
  };

  const selectedMerchants = useMemo(
    () => items.filter((m) => selectedIds.has(m.merchant_id)),
    [items, selectedIds],
  );

  const onCancelSuccess = (resp) => {
    setCancelModalOpen(false);
    setSelectedIds(new Set());
    setReloadKey((k) => k + 1);
    // Lightweight feedback — toast lives at app level, but keep this
    // self-contained so the page works on /admin/merchants standalone.
    if (typeof window !== "undefined") {
      // No-op visual cue; the list refresh shows the cancelled rows
      // disappearing from the default view (and reappearing in the
      // Cancelled filter). Detailed status feedback is in audit logs.
      console.info(
        `[cancel] ${resp.cancelled_count} merchant(s) cancelled `
        + `(bulk_action_id=${resp.bulk_action_id})`
      );
    }
  };

  return (
    <div data-testid="admin-merchants-page" className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Merchants</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
            Merchant applications
          </h1>
          <p className="mt-2 text-[13px] text-zinc-400">
            {items.length} application{items.length === 1 ? "" : "s"}
            {filter === "" && (
              <span className="text-zinc-600"> · cancelled hidden</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[12px] flex-wrap" data-testid="admin-merchants-filter-bar">
          <Filter className="h-3 w-3 text-zinc-500" />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`admin-filter-${f.key || "all"}`}
              className={`rounded-full border px-2.5 py-1 transition-colors ${
                filter === f.key
                  ? "bg-white/[0.06] border-white/[0.15] text-white"
                  : "bg-white/[0.02] border-white/[0.06] text-zinc-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk-action bar — appears once anything is selected */}
      {someSelected && (
        <div
          data-testid="admin-bulk-action-bar"
          className="mt-5 flex items-center justify-between rounded-2xl border border-rose-400/25 bg-rose-400/[0.04] px-4 py-3"
        >
          <div className="text-[12.5px] text-rose-100">
            <span className="font-medium" data-testid="admin-bulk-selected-count">
              {selectedIds.size}
            </span>{" "}
            selected
            <span className="text-rose-200/60 ml-2">
              · cancellation is reversible only by re-creating the merchant
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              data-testid="admin-bulk-clear-btn"
              className="rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-zinc-200 px-3 py-1.5 text-[12px] transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setCancelModalOpen(true)}
              data-testid="admin-bulk-cancel-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/40 bg-rose-400/[0.10] hover:bg-rose-400/[0.20] text-rose-100 px-4 py-1.5 text-[12.5px] font-medium transition-colors"
            >
              <XCircle className="h-3.5 w-3.5" />
              Cancel selected ({selectedIds.size})
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-[13px]">No merchants match this filter.</div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-white/[0.02] border-b border-white/[0.06]">
              <tr className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="pl-5 pr-2 py-3 w-9">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    onChange={toggleAll}
                    disabled={selectableItems.length === 0}
                    data-testid="admin-bulk-select-all"
                    aria-label="Select all cancellable merchants"
                    className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 accent-rose-400 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </th>
                <th className="px-3 py-3 w-9" title="Compliance verdict">✓ / ⚠ / ✗</th>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Reference</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3" title="Shop credentials lifecycle">Creds</th>
                <th className="px-5 py-3">SLA</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const isCancellable = CANCELLABLE_STATES.has(m.status);
                const isSelected = selectedIds.has(m.merchant_id);
                return (
                  <tr
                    key={m.merchant_id}
                    data-testid={`admin-merchant-row-${m.merchant_id}`}
                    className={`border-b border-white/[0.04] last:border-0 transition-colors ${
                      isSelected ? "bg-rose-400/[0.04]" : "hover:bg-white/[0.025]"
                    }`}
                  >
                    <td className="pl-5 pr-2 py-3.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(m.merchant_id)}
                        disabled={!isCancellable}
                        title={isCancellable ? "" : `Cannot cancel: ${m.status}`}
                        data-testid={`admin-row-select-${m.merchant_id}`}
                        aria-label={`Select ${m.company_name || m.merchant_id}`}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-white/5 accent-rose-400 cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-3 py-3.5" data-testid={`admin-merchant-verdict-${m.merchant_id}`}>
                      <VerdictIcon verdict={verdicts[m.merchant_id]} />
                    </td>
                    <td className="px-5 py-3.5 text-[13px] text-white">{m.company_name || "—"}</td>
                    <td className="px-5 py-3.5 text-[12.5px] text-zinc-400">{m.merchant_email || "—"}</td>
                    <td className="px-5 py-3.5 ref-mono text-[11.5px] text-zinc-500">{m.ref}</td>
                    <td className="px-5 py-3.5"><StatusPill status={m.status} size="sm" /></td>
                    <td className="px-5 py-3.5">
                      {m.shop_creds_state === "pending_reveal" ? (
                        <span
                          data-testid={`row-creds-pending-${m.merchant_id}`}
                          title={m.shop_creds_issued_at
                            ? `Credentials issued ${new Date(m.shop_creds_issued_at).toLocaleDateString()} — merchant hasn't opened the Gateway page yet.`
                            : "Credentials issued — merchant hasn't revealed them yet."}
                          className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/[0.06] text-amber-200 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.12em]"
                        >
                          ● Pending reveal
                        </span>
                      ) : m.shop_creds_state === "revealed" ? (
                        <span
                          data-testid={`row-creds-revealed-${m.merchant_id}`}
                          title={m.shop_creds_revealed_at
                            ? `Revealed ${new Date(m.shop_creds_revealed_at).toLocaleString()}`
                            : "Credentials revealed."}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-400/[0.05] text-emerald-200 px-2 py-0.5 text-[10.5px] uppercase tracking-[0.12em]"
                        >
                          ● Revealed
                        </span>
                      ) : (
                        <span className="text-zinc-700 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {m.sla && m.sla.state !== "not_applicable" ? (
                        <SLAPill sla={m.sla} size="sm" testId={`row-sla-${m.merchant_id}`} />
                      ) : (
                        <span className="text-zinc-700 text-[11px]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-[12px] text-zinc-500">
                      {new Date(m.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3.5">
                      <Link
                        to={`/admin/merchants/${m.merchant_id}`}
                        data-testid={`admin-merchant-open-${m.merchant_id}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-white/[0.08] text-zinc-400 hover:bg-white/[0.06] hover:text-white"
                      >
                        <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {cancelModalOpen && (
        <CancelMerchantsModal
          merchants={selectedMerchants}
          onClose={() => setCancelModalOpen(false)}
          onSuccess={onCancelSuccess}
          testId="bulk-cancel-modal"
        />
      )}
    </div>
  );
}
